import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  computed,
  effect,
  ElementRef,
  inject,
  Injector,
  input,
  OnInit,
  output,
  signal,
  untracked,
  ViewChild,
} from '@angular/core'
import {
  SimpleFilterChangeEvent,
  SimpleFilterConfiguration,
  SimpleFilterType,
} from '../../data/interfaces'
import { AsyncPipe } from '@angular/common'
import { MatIconModule } from '@angular/material/icon'
import { FormsModule } from '@angular/forms'
import { SimpleFilterState } from '../../data/simple-filter.state'
import * as Apollo from 'apollo-angular'
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy'
import { ApolloQueryResult } from '@apollo/client'
import { debounceTime, distinctUntilChanged, Subject, tap } from 'rxjs'
import { map } from 'rxjs/operators'
import { MatMenuItem } from '@angular/material/menu'
import { AutocompleteDropdownComponent } from '@wm-front/shared/autocomplete'
import {
  DateRangePickerComponent,
  InputComponent,
} from '@wm-front/shared/forms'
import { DropdownOption } from '@wm-front/core'
import { GqlPageInfos } from '@wm-front/shared/interfaces'
import { ButtonComponent } from '@wm-front/shared/buttons'
import { DateFilter } from '@wm-front/admin/graphql'
import { L10nService } from '@wm-front/admin/l10n'

type ArrayValueType = string[] | number[]

type ValueType =
  | string
  | ArrayValueType
  | boolean
  | undefined
  | { after?: string; before?: string }

@UntilDestroy()
@Component({
  selector: 'wm-front-simple-filter',
  imports: [
    AsyncPipe,
    AutocompleteDropdownComponent,
    ButtonComponent,
    MatIconModule,
    FormsModule,
    InputComponent,
    DateRangePickerComponent,
    MatMenuItem,
  ],
  templateUrl: './simple-filter.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./simple-filter.component.scss'],
})
export class SimpleFilterComponent<T> implements OnInit {
  @ViewChild('dropdownButton', { read: ElementRef })
  public buttonElement: ElementRef<HTMLButtonElement>

  /** The configuration setter */
  public configuration = input.required<SimpleFilterConfiguration<T>>()

  /** The inputted value */
  public value = input<ValueType>()

  /** So the outside of this component can tell it to reset if needed */
  public reset = input<Subject<void>>()

  /** Will tell you if the display is in responsive mode */
  public isResponsive = input<boolean>()

  public displayListInline = input<boolean>(false)

  /** A writable signal to be able to manage the value */
  public valueHolder = signal<ValueType>(undefined)

  public valueHolderAsDateFilter = computed(
    () => this.valueHolder() as DateFilter
  )

  public valueHolderAsArray = computed(
    () => this.valueHolder() as ArrayValueType
  )

  /** Will be used to emit when the filter change */
  public changeEvent = output<SimpleFilterChangeEvent>()

  /** Will be used to emit when the open state change */
  public openEvent = output<boolean>()

  /** Will state if the dropdown if any should be opened */
  public isOpen = signal(false)

  /** It is the list of options for the filters */
  public options: DropdownOption[] = []

  /** To expose the enumeration in the template */
  public SimpleFilterType = SimpleFilterType

  public state = inject(SimpleFilterState)

  /** Minimum search length before a query is sent: 2 characters, except in Hong Kong where a single character is already meaningful */
  public minSearchLength = computed(
    () =>
      this.configuration().minSearchLength ??
      (this._l10nService.territory$() === 'HK' ? 1 : 2)
  )

  public isRadioButtonModeAndAsValue = computed(
    () =>
      this.configuration()?.radioButtonMode &&
      JSON.stringify(this.valueHolder()) !== '[]' &&
      this.valueHolder() !== undefined
  )

  public helpersAreOpen = signal(false)

  public helpers = computed(() =>
    this.configuration().helpers?.filter(
      (helper) =>
        !this.valueHolder() ||
        (typeof this.valueHolder() === 'string' &&
          helper.value?.includes(this.valueHolder() as string))
    )
  )

  protected readonly String = String

  private _l10nService = inject(L10nService)

  private _cdr = inject(ChangeDetectorRef)

  private _injector = inject(Injector)

  /** Subject triggered on infinite scroll, linked to a debounce */
  private infiniteScroll$: Subject<void> = new Subject()

  /** Subject triggered on new search, linked to a debounce */
  private search$: Subject<void> = new Subject<void>()

  private search = signal('')

  private setValueOnlyOnce = true

  public ngOnInit(): void {
    this.manageInfiniteScroll()
    this.manageConfigurationChange()
    this.manageValueChange()
    this.manageOpenStateChange()
    this.manageReset()
    this.manageSearch()
  }

  /**
   * What will happen when the form change
   */
  public onChange(): void {
    this.changeEvent.emit({
      key: this.configuration().key as string,
      value: this.configuration().valueConverter
        ? (
            this.configuration().valueConverter as (
              value: any,
              values: DropdownOption[]
            ) => any
          )(this.valueHolder(), this.options)
        : this.valueHolder(),
    })
  }

  /**
   * What happen when the user click on the button
   */
  public openDropdownAndFetchData(): void {
    this.search.set('')
    this.isOpen.set(!this.isOpen())
    if (this.isOpen() && this.configuration().dataGetter) {
      this.fetchData()
    }
    this._cdr.markForCheck()
  }

  /**
   * What will happen when the user is search for some options
   * @param {string} search
   */
  public handleQueryChanged(search: string): void {
    if (
      this.configuration().dataGetter &&
      !this.configuration().withoutBackendFiltering
    ) {
      this.search.set(search)
      this.fetchData(true)
    }
  }

  /**
   * What will happen when an option was selected
   * @param {DropdownOption} selected
   */
  public handleOptionPicked(selected: DropdownOption): void {
    if (typeof selected.value === 'boolean') {
      this.handleBooleanValueSelected(selected)
    } else {
      this.handleStringArrayValueSelected(
        selected,
        this.configuration()?.radioButtonMode
      )
    }
  }

  public handlePeriodPicked(selected: {
    after?: string
    before?: string
  }): void {
    this.valueHolder.set(selected)
    this.onChange()
  }

  public onPeriodFilterReset(): void {
    this.valueHolder.set('')
    this.onChange()
  }

  /**
   * When we want to close the dropdown
   */
  public closeAutocompletePanel(): void {
    this.isOpen.set(false)
  }

  /**
   * What should happen if the option are resetted
   */
  public handleResetOptionsSelected(): void {
    this.valueHolder.set([])
    if (this.configuration().dataGetter) {
      this.state.updateSelected(this.configuration().key as string, [])
    } else {
      this.options.forEach((option) => (option.checked = false))
      this.options = [...this.options]
    }
    this.onChange()
  }

  /** Handle fetch to make on infinite scroll */
  public handleInfiniteScroll(): void {
    this.infiniteScroll$.next()
  }

  /** Handle fetch to make on infinite scroll */
  public handleSearch(): void {
    if (this.valueHolder() === null) {
      this.valueHolder.set('')
    }
    this.search$.next()
  }

  public onTextFocus(): void {
    if (this.helpers()?.length) {
      this.helpersAreOpen.set(true)
    }
  }

  public onTextBlur(): void {
    this.helpersAreOpen.set(false)
  }

  public onHelperSelected(value: string): void {
    this.valueHolder.set(value)
  }

  public alignsRight(): boolean {
    const button = this.buttonElement.nativeElement
    const boundingRight = button.getBoundingClientRect().right
    const documentWidth = document.body.scrollWidth
    const filterDropdownWidth = 380
    const minMarginRight = 300

    return (
      boundingRight > filterDropdownWidth &&
      documentWidth - boundingRight < minMarginRight
    )
  }

  private handleStringArrayValueSelected(
    selected: DropdownOption,
    asMonoValue?: boolean
  ): void {
    const value = selected.value as string
    if (!this.valueHolder()) {
      this.valueHolder.set([])
    }
    const valueIsAlreadySelected = (this.valueHolder() as string[]).includes(
      value
    )
    if (!valueIsAlreadySelected || !this.configuration().required) {
      if (!valueIsAlreadySelected) {
        this.valueHolder.set(
          !asMonoValue ? [...(this.valueHolder() as string[]), value] : [value]
        )
      } else {
        this.valueHolder.set(
          !asMonoValue
            ? [...(this.valueHolder() as string[]).filter((v) => v !== value)]
            : []
        )
      }
      if (this.configuration().dataGetter) {
        this.state.updateSelected(
          this.configuration().key as string,
          this.valueHolder() as string[] | number[]
        )
      } else {
        this.options.forEach(
          (option) =>
            (option.checked = (this.valueHolder() as string[]).includes(
              option.value as string
            ))
        )
      }
      this.options = [...this.options]
      this.onChange()
    }
  }

  /**
   * What to do when a boolean value is selected
   * @param selected
   * @private
   */
  private handleBooleanValueSelected(selected: DropdownOption): void {
    const value = selected.value as boolean
    const valueSelectedIsAlreadySelected = this.valueHolder() === value
    if (!valueSelectedIsAlreadySelected || !this.configuration().required) {
      if (valueSelectedIsAlreadySelected) {
        this.valueHolder.set([])
      } else {
        this.valueHolder.set(value)
      }
      this.options.forEach(
        (option) => (option.checked = this.valueHolder() === option.value)
      )
      this.options = [...this.options]
      this.onChange()
    }
  }

  /**
   * Will manage the search
   * @private
   */
  private manageSearch(): void {
    this.search$
      .pipe(
        debounceTime(300),
        tap(() => this.onChange()),
        untilDestroyed(this)
      )
      .subscribe()
  }

  /**
   * Will manage the infinite scroll
   * @private
   */
  private manageInfiniteScroll(): void {
    this.infiniteScroll$
      .pipe(
        debounceTime(500),
        tap(() => this.fetchData(false)),
        untilDestroyed(this)
      )
      .subscribe()
  }

  /**
   * Set the configuration effect
   * @private
   */
  private manageConfigurationChange(): void {
    effect(
      () => {
        if (this.configuration().type === SimpleFilterType.DROPDOWN) {
          this.setOptions()
        } else {
          this.options = []
        }
      },
      { injector: this._injector }
    )
  }

  /**
   * Set the value effect
   * @private
   */
  private manageValueChange(): void {
    effect(
      () => {
        if (this.setValueOnlyOnce) {
          this.setValue(this.value())
          this.setValueOnlyOnce = false
        }
      },
      {
        injector: this._injector,
      }
    )
  }

  /**
   * Set the open effect
   * @private
   */
  private manageOpenStateChange(): void {
    effect(() => this.openEvent.emit(this.isOpen()), {
      injector: this._injector,
    })
  }

  /**
   * Will know what to do when the reset event is emitted
   * @private
   */
  private manageReset(): void {
    this.reset()
      ?.pipe(
        tap(() => {
          if (this.configuration().type === SimpleFilterType.DROPDOWN) {
            this.handleResetOptionsSelected()
          } else {
            this.valueHolder.set('')
          }
        }),
        untilDestroyed(this)
      )
      .subscribe()
  }

  /**
   * Will fetch data if need be
   * @param {boolean} reset
   * @param search
   * @private
   */
  private fetchData(reset = true): void {
    this.state
      .fetch(
        this.configuration().key as string,
        this.configuration().dataGetter as Apollo.Query<any, any>,
        this.configuration().dropdownOptionConverter as (
          data: ApolloQueryResult<any>
        ) => DropdownOption[],
        this.configuration().paginationConverter as (
          data: ApolloQueryResult<T>
        ) => GqlPageInfos,
        reset,
        this.configuration().bodyConverter,
        this.configuration().filters,
        this.configuration().defaultPageSize,
        this.search(),
        this.configuration()?.fetchSelectedDataBodyConverter
          ? this.configuration().fetchSelectedDataBodyConverter!(
              this.valueHolder(),
              this.state.getState(),
              this.state.externalState?.getState()
            )
          : undefined
      )
      .subscribe()
  }

  /**
   * Will set the options, or how to get it
   * @private
   */
  private setOptions(): void {
    if (this.configuration().dataGetter) {
      this.setOptionAsynchronously()
    } else {
      this.setOptionSynchronously()
    }
  }

  /**
   * What should happen when the data is asynchronous
   * @private
   */
  private setOptionAsynchronously(): void {
    this.state
      .data$(this.configuration().key as string)
      .pipe(
        map((res) => res || []),
        tap((res) => (this.options = res as DropdownOption[])),
        distinctUntilChanged(
          (previous, next) => JSON.stringify(previous) === JSON.stringify(next)
        ),
        tap(() =>
          this.state.updateSelected(
            this.configuration().key as string,
            this.valueHolder() as string[] | number[]
          )
        ),
        untilDestroyed(this)
      )
      .subscribe()
  }

  /**
   * What should happen when the data is directly provided
   * @private
   */
  private setOptionSynchronously(): void {
    this.options = this.configuration().data || []
  }

  /**
   * Will know how to set the value
   * @param value
   * @private
   */
  private setValue(value: ValueType): void {
    this.valueHolder.set(value)
    if (
      this.configuration().type === SimpleFilterType.DROPDOWN &&
      this.configuration().data?.length
    ) {
      this.options.forEach((option) => {
        if (typeof value === 'boolean') {
          option.checked = value === option.value
        } else {
          option.checked = !!(value as any[])?.some(
            (value) => option.value === value
          )
        }
      })
    }
    if (this.value() === undefined) {
      const defaultValue = untracked(this.configuration).data?.find(
        (d) => d.checked
      )
      if (defaultValue) {
        this.handleOptionPicked(defaultValue)
      }
    }
  }
}
